import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, User, ArrowRight, X } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";

/**
 * Reusable "OR SEARCH ANY PATIENT" header card.
 *
 * Renders a label + a large search input that hits GET /api/patients?q=… and shows live results.
 * Clicking a result calls `onPick(patient)` if provided, otherwise navigates to /app/patients/{id}.
 *
 * Props:
 *   label        — section label (default "OR SEARCH ANY PATIENT")
 *   placeholder  — input placeholder
 *   onPick       — optional callback(patient) when a result is selected. If absent, default navigation.
 *   compact      — when true, renders the smaller variant suitable for inline page headers.
 *   testIdRoot   — root for data-testids (default "patient-search-header")
 */
export default function PatientSearchHeader({
  label = "OR SEARCH ANY PATIENT",
  placeholder = "Type name, UHID or mobile (min 2 chars)…",
  onPick,
  compact = false,
  crossBranch = false,
  testIdRoot = "patient-search-header",
}) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const doSearch = async (val) => {
    setQ(val);
    if (val.length < 2) { setItems([]); setOpen(false); return; }
    try {
      const { data } = await api.get(`/patients?q=${encodeURIComponent(val)}&limit=10${crossBranch ? "&cross_branch=true" : ""}`);
      setItems(data.items || []);
      setOpen(true);
    } catch { /* ignore */ }
  };

  const handlePick = (p) => {
    setOpen(false);
    setQ("");
    setItems([]);
    if (onPick) onPick(p);
    else navigate(`/app/patients/${p.id}`);
  };

  return (
    <div className={`relative ${compact ? "" : "rounded-2xl border border-border bg-white p-5 shadow-sm"}`} ref={wrapRef} data-testid={testIdRoot}>
      <div className={`mb-2 font-heading text-xs font-bold uppercase tracking-widest text-brand`}>{label}</div>
      <div className="relative">
        <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle" />
        <Input
          value={q}
          onChange={(e) => doSearch(e.target.value)}
          placeholder={placeholder}
          className={`pl-10 ${compact ? "h-9" : "h-12 text-base"}`}
          data-testid={`${testIdRoot}-input`}
          onFocus={() => { if (items.length > 0) setOpen(true); }}
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(""); setItems([]); setOpen(false); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-subtle hover:text-ink"
            data-testid={`${testIdRoot}-clear`}
            aria-label="Clear search"
          ><X size={14} /></button>
        )}
      </div>

      {open && items.length > 0 && (
        <div className="absolute left-0 right-0 z-30 mt-1 max-h-72 overflow-y-auto rounded-md border border-border bg-white shadow-lg" data-testid={`${testIdRoot}-results`}>
          {items.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => handlePick(p)}
              className="flex w-full items-center justify-between px-3 py-2 text-left transition hover:bg-secondary"
              data-testid={`${testIdRoot}-result-${p.uhid}`}
            >
              <div className="flex items-center gap-2.5">
                <div className="grid h-8 w-8 place-items-center rounded-full bg-brand/10 text-brand"><User size={14} /></div>
                <div className="text-sm">
                  <div className="font-medium text-ink">{p.name}</div>
                  <div className="text-[11px] text-ink-muted">UHID {p.uhid} · {p.mobile}{p.age ? ` · ${p.age}y` : ""}</div>
                </div>
              </div>
              <ArrowRight size={14} className="text-brand" />
            </button>
          ))}
        </div>
      )}
      {open && q.length >= 2 && items.length === 0 && (
        <div className="absolute left-0 right-0 z-30 mt-1 rounded-md border border-border bg-white px-3 py-2 text-xs text-ink-muted shadow-lg" data-testid={`${testIdRoot}-results-empty`}>
          No patients match "{q}".
        </div>
      )}
    </div>
  );
}
